import { useState } from 'react';
import { AlertCircle, Lock, Shield, Loader2, RotateCcw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Switch } from '@/app/components/ui/switch';
import { Skeleton } from '@/app/components/ui/skeleton';
import { Badge } from '@/app/components/ui/badge';
import { Button } from '@/app/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/app/components/ui/dialog';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Alert, AlertDescription } from '@/app/components/ui/alert';
import { toast } from '@/app/components/ui/simple-toaster';
import { useUserCapabilities, useUpdateUserCapability } from '@/hooks/use-capabilities';
import { useVerifyPassword } from '@/hooks/use-admin';
import { handleApiError } from '@/lib/api-client';
import type { CapabilityModule, UpdateUserCapabilityPayload, UserCapability } from '@/lib/types';

const MODULE_LABELS: Record<CapabilityModule, string> = {
  core: 'Core',
  inventory: 'H3Gear',
  safety: 'H3Safe',
  crew: 'H3Crew',
  projects: 'H3Books',
  payroll: 'H3Pay',
  compliance: 'H3Comply',
  work: 'H3Project',
};

const MODULE_ORDER: CapabilityModule[] = [
  'core',
  'inventory',
  'safety',
  'crew',
  'projects',
  'payroll',
  'compliance',
  'work',
];

interface PermissionsTabProps {
  userId: number | null | undefined;
}

interface PendingChange {
  capability: UserCapability;
  enabled?: boolean;
  reset?: boolean;
}

export function PermissionsTab({ userId }: PermissionsTabProps) {
  const { data, isLoading, error } = useUserCapabilities(userId);
  const updateCapability = useUpdateUserCapability();
  const verifyPassword = useVerifyPassword();

  const [modalOpen, setModalOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingChange | null>(null);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-48" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-sm text-red-700 bg-red-50 rounded-md border border-red-200">
        Failed to load permissions. {handleApiError(error).message}
      </div>
    );
  }

  const groups = data ?? {};
  const modules = MODULE_ORDER.filter((m) => groups[m]?.length);
  // Org Admins have full access; the server locks their rows and reports
  // them as enabled, so the toggles are shown as full access and disabled.
  const adminLocked = modules.some((m) =>
    (groups[m] ?? []).some((item) => item.admin_locked),
  );

  if (modules.length === 0) {
    return (
      <div className="text-center py-12 text-gray-600">
        No capabilities found for this user.
      </div>
    );
  }

  const handleToggle = (capability: UserCapability, enabled: boolean) => {
    setPending({ capability, enabled });
    setPassword('');
    setErrorMsg(null);
    setModalOpen(true);
  };

  const handleReset = (capability: UserCapability) => {
    setPending({ capability, reset: true });
    setPassword('');
    setErrorMsg(null);
    setModalOpen(true);
  };

  const handleCancel = () => {
    setModalOpen(false);
    setPending(null);
    setPassword('');
    setErrorMsg(null);
  };

  const handleConfirm = async () => {
    if (!pending) return;
    setErrorMsg(null);
    if (!password) {
      setErrorMsg('Password is required.');
      return;
    }

    try {
      const verifyResp = await verifyPassword.mutateAsync({ password });
      if (!verifyResp.valid || !verifyResp.step_up_token) {
        setErrorMsg('Current password is incorrect.');
        return;
      }

      const updateData: UpdateUserCapabilityPayload = {
        step_up_token: verifyResp.step_up_token,
      };
      if (pending.reset) {
        updateData.reset = true;
      } else {
        updateData.enabled = pending.enabled;
        updateData.is_override = true;
      }

      await updateCapability.mutateAsync({
        id: pending.capability.id,
        data: updateData,
      });

      toast.success(
        pending.reset
          ? `${pending.capability.capability.name} reset to default`
          : `${pending.capability.capability.name} ${pending.enabled ? 'enabled' : 'disabled'}`
      );
      setModalOpen(false);
      setPending(null);
      setPassword('');
    } catch (err: unknown) {
      setErrorMsg(handleApiError(err as Error).message);
    }
  };

  return (
    <div className="space-y-4">
      {adminLocked ? (
        <Alert data-testid="admin-full-access-note">
          <Lock className="h-4 w-4" />
          <AlertDescription>
            Admins have full access — change their role to restrict them.
          </AlertDescription>
        </Alert>
      ) : (
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Shield className="h-4 w-4" />
          <span>
            Toggle capabilities to override the default role-based access for this user.
          </span>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {modules.map((module) => (
          <Card key={module}>
            <CardHeader>
              <CardTitle className="text-base">{MODULE_LABELS[module]}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {groups[module]?.map((item) => (
                <div
                  key={item.id}
                  className="flex items-start justify-between gap-3"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {item.capability.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {item.capability.action}
                    </p>
                    <div className="flex items-center gap-1 mt-1 flex-wrap">
                      <Badge variant="outline" className="text-xs">
                        {item.capability.default_min_role}
                      </Badge>
                      {item.admin_locked ? (
                        <Badge variant="outline" className="text-xs text-green-700 border-green-200 bg-green-50">
                          Full access
                        </Badge>
                      ) : item.is_override ? (
                        <Badge variant="secondary" className="text-xs">
                          Overridden
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs text-green-700 border-green-200 bg-green-50">
                          Default
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={item.admin_locked ? true : item.enabled}
                      onCheckedChange={(checked) => handleToggle(item, checked)}
                      disabled={
                        item.admin_locked ||
                        updateCapability.isPending ||
                        verifyPassword.isPending
                      }
                      aria-label={`Toggle ${item.capability.name}`}
                    />
                    {item.is_override && !item.admin_locked && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleReset(item)}
                        disabled={updateCapability.isPending || verifyPassword.isPending}
                        aria-label={`Reset ${item.capability.name} to default`}
                        title="Reset to default"
                      >
                        <RotateCcw className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog
        open={modalOpen}
        onOpenChange={(open) => {
          if (!open && (verifyPassword.isPending || updateCapability.isPending)) {
            return;
          }
          setModalOpen(open);
        }}
      >
        <DialogContent
          className="sm:max-w-sm"
          onPointerDownOutside={(e) => {
            if (verifyPassword.isPending || updateCapability.isPending) {
              e.preventDefault();
            }
          }}
          onEscapeKeyDown={(e) => {
            if (verifyPassword.isPending || updateCapability.isPending) {
              e.preventDefault();
            }
          }}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5" />
              Confirm Password
            </DialogTitle>
            <DialogDescription>
              Re-enter your password to update this permission.
            </DialogDescription>
          </DialogHeader>

          {errorMsg && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{errorMsg}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="confirm-password">Current Password</Label>
            <Input
              id="confirm-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={verifyPassword.isPending || updateCapability.isPending}
              className="max-w-sm"
              autoFocus
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={verifyPassword.isPending || updateCapability.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleConfirm}
              disabled={
                !password || verifyPassword.isPending || updateCapability.isPending
              }
            >
              {(verifyPassword.isPending || updateCapability.isPending) && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
